import { useEffect, useState } from "react";
import { X, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { adsSpyService } from "../services/adsSpy.service";
import type { SpyAd, AiAnalysis } from "../types/adsSpy.types";
import { parseAiAnalysis, scoreColor } from "../utils/helpers";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface AdDetailModalProps {
  open: boolean;
  onClose: () => void;
  adId: number | null;
}

export default function AdDetailModal({
  open,
  onClose,
  adId,
}: AdDetailModalProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;
  const [ad, setAd] = useState<SpyAd | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    if (!open || !adId) {
      setAd(null);
      return;
    }
    setLoading(true);
    adsSpyService
      .getAd(adId)
      .then((data) => setAd(data))
      .catch(() => setAd(null))
      .finally(() => setLoading(false));
  }, [open, adId]);

  const handleAnalyze = async () => {
    if (!ad) return;
    setAnalyzing(true);
    try {
      const result = await adsSpyService.analyzeAd(ad.id);
      setAd({ ...ad, ai_analysis: result.data as unknown as SpyAd["ai_analysis"] });
    } catch (err) {
      console.error("Error analyzing ad:", err);
    } finally {
      setAnalyzing(false);
    }
  };

  const analysis = ad
    ? (parseAiAnalysis(
        ad.ai_analysis as string | Record<string, unknown> | null
      ) as AiAnalysis | null)
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-[20px] bg-white shadow-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-5 h-5 animate-spin text-fluxi" />
          </div>
        ) : !ad ? (
          <div className="p-8 text-center text-red-500">
            {t("error-loading-details", "Error loading details")}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row">
            {/* Media */}
            <div className="md:w-[45%] p-5 border-r border-gray-200">
              {ad.video_url ? (
                <video
                  src={ad.video_url}
                  controls
                  className="w-full rounded-xl"
                  poster={ad.thumbnail_url || ad.image_url || undefined}
                />
              ) : ad.image_url ? (
                <img
                  src={ad.image_url}
                  alt=""
                  className="w-full rounded-xl"
                />
              ) : (
                <div className="py-20 text-center text-gray-400 text-sm">
                  {t("no-media-available", "No media available")}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="md:w-[55%] p-6 overflow-y-auto max-h-[80vh]">
              {/* Header */}
              <div className="flex items-start justify-between mb-5">
                <div>
                  <div className="text-[11px] font-medium text-gray-500 mb-1.5">
                    {ad.brand_name}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 leading-snug">
                    {ad.headline || t("no-headline", "No headline")}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Ad copy */}
              {ad.ad_copy && (
                <div className="mb-5">
                  <label className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                    {t("text", "Text")}
                  </label>
                  <p className="text-[13px] mt-1.5 leading-relaxed text-gray-600 whitespace-pre-wrap">
                    {ad.ad_copy}
                  </p>
                </div>
              )}

              {/* Metadata grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                {ad.cta_text && (
                  <MetaCard label={t("call-to-action", "Call to action")} value={ad.cta_text} />
                )}
                {ad.media_type && (
                  <MetaCard label={t("type", "Type")} value={ad.media_type} />
                )}
                {ad.impression_rank > 0 && (
                  <MetaCard
                    label={t("position", "Position")}
                    value={`#${ad.impression_rank}`}
                  />
                )}
                {ad.start_date && (
                  <MetaCard label={t("start", "Start")} value={ad.start_date} />
                )}
              </div>

              {/* Links */}
              <div className="flex flex-col gap-1.5 mb-5">
                {ad.landing_page_url && (
                  <a
                    href={ad.landing_page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[13px] font-medium text-fluxi hover:underline inline-flex items-center gap-1"
                  >
                    {t("view-landing-page", "View landing page")}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {ad.page_id && (
                  <a
                    href={`https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=${ad.country || "US"}&view_all_page_id=${ad.page_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[13px] font-medium text-fluxi hover:underline inline-flex items-center gap-1"
                  >
                    {t("view-all-brand-ads", "View all ads from {name}").replace("{name}", ad.brand_name)}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {ad.library_id && (
                  <a
                    href={`https://www.facebook.com/ads/library/?id=${ad.library_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-400 hover:underline inline-flex items-center gap-1"
                  >
                    {t("view-individual-ad", "View individual ad")}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* AI Analysis section */}
              <div className="border-t border-gray-200 pt-5">
                {analysis ? (
                  <AnalysisDisplay analysis={analysis} />
                ) : (
                  <button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    className="w-full text-[13px] font-semibold py-2.5 rounded-xl bg-fluxi text-white hover:bg-fluxi-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t("analyzing", "Analyzing...")}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        {t("analyze-with-ai", "Analyze with AI")}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MetaCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl p-3 bg-[#F5F6FA]">
      <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
        {label}
      </div>
      <div className="text-[13px] font-medium text-gray-900 mt-1">
        {value}
      </div>
    </div>
  );
}

function AnalysisDisplay({ analysis }: { analysis: AiAnalysis }) {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;
  const score =
    analysis.score != null ? Number(analysis.score) : null;

  const improvementItems = analysis.improvements
    ? String(analysis.improvements)
        .split(/(?:\d+\.\s*|[-\u2022]\s+)/)
        .filter((s) => s.trim())
    : [];

  return (
    <div className="rounded-2xl p-4 bg-[#F5F6FA]">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[13px] font-semibold text-gray-900">
          {t("ai-analysis", "AI Analysis")}
        </span>
        {score !== null && (
          <span
            className="text-sm font-bold"
            style={{ color: scoreColor(score) }}
          >
            {score}/10
          </span>
        )}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {analysis.asset_type && <AiTag value={analysis.asset_type} />}
        {analysis.visual_format && (
          <AiTag value={analysis.visual_format} />
        )}
        {analysis.messaging_angle && (
          <AiTag value={analysis.messaging_angle} />
        )}
        {analysis.hook_tactic && (
          <AiTag value={analysis.hook_tactic} />
        )}
        {analysis.offer_type && <AiTag value={analysis.offer_type} />}
      </div>

      {/* Summary */}
      {analysis.summary && (
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-1">
            {t("summary", "Summary")}
          </div>
          <p className="text-xs leading-relaxed text-gray-600">
            {analysis.summary}
          </p>
        </div>
      )}

      {/* Improvements */}
      {improvementItems.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-[11px] font-medium uppercase tracking-wider text-amber-600 mb-2">
            {t("suggested-improvements", "Suggested improvements")}
          </div>
          <ul className="space-y-1.5">
            {improvementItems.map((item, i) => (
              <li
                key={i}
                className="text-xs leading-relaxed text-gray-500 flex items-start gap-2"
              >
                <span className="text-amber-600 mt-0.5 shrink-0">
                  &#8227;
                </span>
                <span>{item.trim()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AiTag({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-[#EEEEFF] text-fluxi border border-[#D8D8F8]">
      {value}
    </span>
  );
}
